/**
 * AuthGuard — T21.
 *
 * Wraps protected routes. On mount, if no access token is in memory, the guard
 * attempts a silent refresh via the HttpOnly __Host-ms_refresh cookie before
 * giving up. This makes direct navigation (e.g. typing /settings into the URL
 * bar, or a hard reload) survive across Zustand state resets.
 *
 * Three terminal states:
 *   - silent refresh succeeded → fetchMe → render children
 *   - silent refresh failed (no cookie, expired, revoked) → redirect to /login
 *   - in flight → show full-page spinner
 */
import { Center, Loader } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../state/auth-store';

// ─── FullPageSpinner ──────────────────────────────────────────────────────────

function FullPageSpinner() {
  return (
    <Center h="100vh" data-testid="full-page-spinner">
      <Loader size="xl" />
    </Center>
  );
}

// ─── AuthGuard ────────────────────────────────────────────────────────────────

interface AuthGuardProps {
  children: React.ReactNode;
}

type BootstrapStatus = 'pending' | 'authed' | 'unauthed';

export function AuthGuard({ children }: AuthGuardProps) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const refresh = useAuthStore((s) => s.refresh);
  const fetchMe = useAuthStore((s) => s.fetchMe);
  const location = useLocation();

  // Tracks the one-time silent-refresh attempt on first mount.
  const [bootstrap, setBootstrap] = useState<BootstrapStatus>(() =>
    accessToken ? 'authed' : 'pending'
  );

  useEffect(() => {
    // Already have a token in memory — make sure the user profile is loaded.
    if (accessToken) {
      setBootstrap('authed');
      if (!user) {
        void fetchMe();
      }
      return;
    }

    // No token — try a silent refresh via the refresh cookie. If it succeeds,
    // refresh() populates the store and we'll re-render with a token. If it
    // fails, mark unauthed and let the redirect run.
    if (bootstrap === 'pending') {
      void (async () => {
        try {
          await refresh();
          // Successful refresh updates accessToken; the next render handles it.
          // But also fetchMe explicitly in case the parent didn't.
          await fetchMe();
          setBootstrap('authed');
        } catch {
          setBootstrap('unauthed');
        }
      })();
    }
  }, [accessToken, user, fetchMe, refresh, bootstrap]);

  // Silent refresh finished and we still have no token → kick to /login.
  if (bootstrap === 'unauthed' && !accessToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Silent refresh in flight, OR token present but profile still loading.
  if (bootstrap === 'pending' || !user) {
    return <FullPageSpinner />;
  }

  return <>{children}</>;
}
